import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { Tooltip } from "PFComponents/tooltip";
import { useActivityEconomicsScenarioClone } from "PFCore/hooks/queries/activity/use_activity_economics_scenario_clone";
import { Activity } from "PFCore/types/activity";
import { EconomicsScenarioDetails } from "PFCore/types/economics_scenarios";
import { Id } from "PFTypes";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useScenarioNavigate } from "../hooks/use_scenario_navigate";
import { RemoveScenarioModal } from "../parts/remove_scenario_modal";
import { ScenarioNameModal } from "../parts/scenario_name_modal";
import { ShareScenarioModal } from "../parts/share_scenario_modal";
import { useEconomicsPermissions } from "../show/hooks/use_economics_permissions";

type ScenarioToolboxProps = {
  activity: Activity;
  scenario: EconomicsScenarioDetails;
  isEditable: boolean;
  activities: Activity[];
  selectedVacancies: Id[];
  hasFillAndBookButton: boolean;
  fillAndBookCounter: number;
  handleFill: () => void;
  handleFillAndBook: () => void;
};

export const ScenarioToolbox = ({
  activity,
  scenario,
  isEditable,
  activities,
  selectedVacancies,
  hasFillAndBookButton,
  fillAndBookCounter,
  handleFill,
  handleFillAndBook
}: ScenarioToolboxProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "scenario" });
  const { canCreateScenarios } = useEconomicsPermissions();
  const { showGrowl } = useGrowlStoreActions();

  const { openScenario, closeScenario } = useScenarioNavigate();
  const { mutate: cloneScenario } = useActivityEconomicsScenarioClone(activity.id.toString());

  const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const closeEditNameModal = useCallback(() => {
    setIsEditNameModalOpen(false);
  }, []);

  const handleClone = () => {
    scenario.id &&
      cloneScenario(scenario.id, {
        onSuccess: (newScenario) => {
          showGrowl({
            kind: "success",
            message: t("cloneSuccess")
          });

          openScenario(newScenario.id);
        },
        onError: () => {
          showGrowl({
            kind: "error",
            message: t("cloneError")
          });
        }
      });
  };

  return (
    <>
      <Inline>
        {!!activities.length &&
          (hasFillAndBookButton ? (
            <Button
              text={t("fillAndBookButton", { count: fillAndBookCounter })}
              onClick={handleFillAndBook}
              disabled={!isEditable || fillAndBookCounter === 0}
            />
          ) : (
            <Button
              text={t("fillButton", { count: selectedVacancies.length })}
              onClick={handleFill}
              disabled={!isEditable || selectedVacancies.length === 0}
            />
          ))}
        {canCreateScenarios && <Button icon="duplicate" kind="secondary" onClick={handleClone} />}
        {isEditable && (
          <>
            <Tooltip content={t("rename")}>
              <Button icon="edit" kind="secondary" onClick={() => setIsEditNameModalOpen(true)} />
            </Tooltip>
            <Button icon="share" kind="secondary" onClick={() => setIsShareModalOpen(true)} />
            <Button icon="remove" kind="secondary" onClick={() => setIsRemoveModalOpen(true)} />
          </>
        )}
      </Inline>

      {isEditNameModalOpen && (
        <ScenarioNameModal
          isEdit
          scenario={scenario}
          onClose={closeEditNameModal}
          onSave={closeEditNameModal}
        />
      )}
      {isRemoveModalOpen && (
        <RemoveScenarioModal
          scenarioId={scenario.id}
          activityId={activity.id}
          onRemove={closeScenario}
          onClose={() => setIsRemoveModalOpen(false)}
        />
      )}
      {isShareModalOpen && (
        <ShareScenarioModal scenarioId={scenario.id} onClose={() => setIsShareModalOpen(false)} />
      )}
    </>
  );
};
